import { useCallback, useEffect, useRef, useState } from 'react'
import { getCustomerService } from '@/services/customers'
import { getAssessmentService } from '@/services/assessments'
import type { Assessment } from '@/types/assessment.types'
import type { AuthorizedAssessmentDetail } from '@/services/assessments/assessments.service'

interface ClientOwnedAssessmentHistoryProps {
  customerId: string
  customerType?: 'individual' | 'business'
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function assessmentVersionKey(item: Assessment): string {
  return `${item.id}:${item.sourceVersionId ?? 'legacy'}`
}

export default function ClientOwnedAssessmentHistory({
  customerId,
  customerType = 'individual',
}: ClientOwnedAssessmentHistoryProps) {
  const [clientCustomerId, setClientCustomerId] = useState<number | null>(null)
  const [resolvingOwner, setResolvingOwner] = useState(true)
  const [sourceMode, setSourceMode] = useState<'current' | 'retained'>('current')
  const [items, setItems] = useState<Assessment[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [started, setStarted] = useState(false)
  const [selectedItem, setSelectedItem] = useState<Assessment | null>(null)
  const [detail, setDetail] = useState<AuthorizedAssessmentDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const detailGeneration = useRef(0)
  const requestGeneration = useRef(0)

  useEffect(() => {
    let active = true
    setResolvingOwner(true)
    setClientCustomerId(null)
    setError(null)
    getCustomerService()
      .resolveClientCustomerId(customerId, customerType)
      .then(ownerId => {
        if (active) setClientCustomerId(ownerId)
      })
      .catch(cause => {
        if (active) setError(cause instanceof Error ? cause.message : 'Owner alias unavailable.')
      })
      .finally(() => {
        if (active) setResolvingOwner(false)
      })
    return () => {
      active = false
    }
  }, [customerId, customerType])

  const loadPage = useCallback(
    async (cursor?: string, generation = requestGeneration.current) => {
      if (clientCustomerId === null) return
      setLoading(true)
      setError(null)
      try {
        const page = await getAssessmentService().listAuthorizedClientOwnedPage(clientCustomerId, {
          sourceMode,
          cursor,
        })
        if (generation !== requestGeneration.current) return
        setItems(previous => {
          const known = new Set(previous.map(assessmentVersionKey))
          const accepted: Assessment[] = []
          for (const item of page.items) {
            const key = assessmentVersionKey(item)
            if (known.has(key)) continue
            known.add(key)
            accepted.push(item)
          }
          return [...previous, ...accepted]
        })
        setNextCursor(page.nextCursor)
        setStarted(true)
      } catch (cause) {
        if (generation === requestGeneration.current) {
          setError(cause instanceof Error ? cause.message : 'Please try again.')
        }
      } finally {
        if (generation === requestGeneration.current) setLoading(false)
      }
    },
    [clientCustomerId, sourceMode]
  )

  const openDetail = useCallback(
    async (item: Assessment) => {
      if (clientCustomerId === null) return
      const generation = ++detailGeneration.current
      setSelectedItem(item)
      setDetail(null)
      setDetailError(null)
      setDetailLoading(true)
      try {
        const result = await getAssessmentService().getAuthorizedClientOwnedDetail(
          clientCustomerId,
          item,
          { sourceMode, customerType }
        )
        if (generation === detailGeneration.current) setDetail(result)
      } catch (cause) {
        if (generation === detailGeneration.current) {
          setDetailError(cause instanceof Error ? cause.message : 'Please try again.')
        }
      } finally {
        if (generation === detailGeneration.current) setDetailLoading(false)
      }
    },
    [clientCustomerId, customerType, sourceMode]
  )

  const closeDetail = () => {
    detailGeneration.current += 1
    setSelectedItem(null)
    setDetail(null)
    setDetailError(null)
    setDetailLoading(false)
  }

  useEffect(() => {
    const generation = ++requestGeneration.current
    detailGeneration.current += 1
    setSelectedItem(null)
    setDetail(null)
    setDetailError(null)
    setDetailLoading(false)
    setItems([])
    setNextCursor(null)
    setStarted(false)
    if (clientCustomerId !== null) void loadPage(undefined, generation)
  }, [loadPage])

  return (
    <section className="space-y-4" aria-label="Client-owned assessment history">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-lg font-semibold text-text-primary">Client-owned assessment history</h3>
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
          Authorized access
        </span>
      </div>
      <p className="text-sm text-text-secondary">
        Read-only assessment metadata shared with this firm by the client.
      </p>
      <div className="flex gap-2" role="group" aria-label="Assessment history source">
        <button
          type="button"
          className={`btn btn-sm ${sourceMode === 'current' ? 'btn-primary' : 'btn-outline'}`}
          aria-pressed={sourceMode === 'current'}
          onClick={() => setSourceMode('current')}
        >
          Current access
        </button>
        <button
          type="button"
          className={`btn btn-sm ${sourceMode === 'retained' ? 'btn-primary' : 'btn-outline'}`}
          aria-pressed={sourceMode === 'retained'}
          onClick={() => setSourceMode('retained')}
        >
          Past work
        </button>
      </div>
      {error && clientCustomerId !== null && (
        <div
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
          role="alert"
        >
          Could not load authorized assessment history: {error}
        </div>
      )}
      {(resolvingOwner || (loading && items.length === 0)) && (
        <div className="card card-elevated p-8 text-center" role="status">
          <span className="loading loading-spinner loading-sm" />
          <p className="text-text-secondary mt-3">
            {resolvingOwner ? 'Checking Customer owner alias…' : 'Checking client authorization…'}
          </p>
        </div>
      )}
      {error && clientCustomerId === null && !resolvingOwner && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          role="status"
        >
          Client-owned assessment history is unavailable because Customer could not provide a
          verified owner ID. The firm-scoped legacy assessment list is not used in this mode.
        </div>
      )}
      {started && items.length === 0 && !error && (
        <div className="card card-elevated p-8 text-center text-text-secondary">
          No authorized assessment history is available.
        </div>
      )}
      {items.length > 0 && (
        <div className="card card-elevated divide-y divide-surface-overlay">
          {items.map(item => (
            <article
              key={assessmentVersionKey(item)}
              className="p-4"
              data-testid="authorized-assessment"
            >
              <div className="flex flex-wrap items-center gap-3">
                <strong className="text-text-primary">{item.itrType}</strong>
                <span className="text-text-secondary">FY {item.financialYear}</span>
                <span className="text-text-secondary">AY {item.assessmentYear}</span>
                <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  {item.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-text-secondary">
                <span>Due {formatDate(item.dueDate)}</span>
                {item.filedDate && <span>Filed {formatDate(item.filedDate)}</span>}
              </div>
              <button
                type="button"
                className="btn btn-outline btn-sm mt-3"
                onClick={() => void openDetail(item)}
              >
                View exact-version details
              </button>
            </article>
          ))}
        </div>
      )}
      {selectedItem && (
        <section
          className="card card-elevated space-y-4 p-5"
          aria-label="Authorized assessment detail"
        >
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-base font-semibold text-text-primary">
              {selectedItem.itrType} · FY {selectedItem.financialYear} detail
            </h4>
            <button type="button" className="btn btn-ghost btn-sm" onClick={closeDetail}>
              Close
            </button>
          </div>
          {detailLoading && <p role="status">Checking exact-version authorization…</p>}
          {detailError && (
            <p className="text-sm text-red-700" role="alert">
              {detailError}
            </p>
          )}
          {detail && (
            <div className="space-y-3 text-sm">
              <p className="text-text-secondary">
                Read-only client tax detail for saved version {detail.sourceVersionId}.
              </p>
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="text-text-secondary">PAN</dt>
                  <dd>{detail.clientData.pan}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Regime</dt>
                  <dd>{detail.clientData.taxRegime}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Gross total income</dt>
                  <dd>₹ {detail.clientData.grossTotalIncome}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Total deductions</dt>
                  <dd>₹ {detail.clientData.totalDeductions}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Taxable income</dt>
                  <dd>₹ {detail.clientData.taxableIncome}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Tax liability</dt>
                  <dd>₹ {detail.clientData.totalTaxLiability}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Tax paid</dt>
                  <dd>₹ {detail.clientData.totalTaxPaid}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Refund</dt>
                  <dd>₹ {detail.clientData.refundAmount}</dd>
                </div>
              </dl>
              <p className="rounded-md bg-amber-50 p-3 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                Detailed deduction schedules, income schedules, and bank details are withheld until
                their supported tax-form schemas and legacy records are reconciled.
              </p>
              {detail.originFirmWorkProduct && (
                <div className="rounded-md border border-surface-overlay p-3">
                  <h5 className="font-medium">This firm’s retained work product</h5>
                  <p>Risk: {detail.originFirmWorkProduct.riskLevel}</p>
                  <p>Work value: ₹ {detail.originFirmWorkProduct.workValue}</p>
                  {detail.originFirmWorkProduct.assignedToName && (
                    <p>Assigned to: {detail.originFirmWorkProduct.assignedToName}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      )}
      {clientCustomerId !== null && (nextCursor || (!started && error)) && (
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => void loadPage(nextCursor ?? undefined)}
          disabled={loading}
        >
          {loading ? 'Loading…' : started ? 'Load more' : 'Retry'}
        </button>
      )}
    </section>
  )
}
